/** Imports */
import {z} from 'zod';


/** Types */
export type Media = z.infer<typeof zMedia>;
export type MediaWithThumbnail = z.infer<typeof zMediaWithThumbnail>;
export type MediaFilters = z.infer<typeof zMediaFilters>;
export type MediaTransformations = z.infer<typeof zMediaTransformations>;
export type MediaSpecials = z.infer<typeof zMediaSpecials>;


/// Zod schemas
export const zMedia = z.object({
    type: z.enum(['image', 'video', 'audio']),
    full: z.string(),
    thumbnail: z.union([z.string(), z.literal('SAME_AS_FULL')]),
    icon: z.union([z.string(), z.literal('SAME_AS_FULL')]),
    videoDuration: z.number().nullable(),
});
export const zMediaFilters = z.object({
    disabled: z.boolean(),
    brightness: z.number(),
    contrast: z.number(),
    saturation: z.number(),
    hue: z.number(),
});
export const zMediaTransformations = z.object({
    rotation: z.number(),
    isFlippedX: z.boolean(),
    isFlippedY: z.boolean(),
});
export const zMediaSpecialsSlicer = z.object({
    numberOfSlices: z.number(),
    infillPercentage: z.number(),
    makeSquare: z.boolean(),
    backgroundFillType: z.enum([
        'blurred',
        'solid',
        'checkerboard',
    ]),
    backgroundFillColor: z.string(),
    backgroundFillBlurRadius: z.number(),
});
export const zMediaSpecials = z.object({
    slicer: zMediaSpecialsSlicer.nullish(),
});
export const zMediaWithThumbnail = z.object({
    id: z.string(),
    media: zMedia.nullable(),
    thumbnail: zMedia.nullable(),
    filters: zMediaFilters.omit({disabled: true}).nullish(),
    transformations: zMediaTransformations.nullish(),
    special: zMediaSpecials.nullish(),
    metadata: z.any().optional(),
});
export const zTemplate = z.object({
    id: z.string(),
    name: z.string(),
    width: z.number().min(1),
    height: z.number().min(1),
    backgroundColor: z.string(),
    layers: z.object({
        id: z.string(),
        elementId: z.string(),
        x: z.number(),
        y: z.number(),
        xAnchor: z.tuple([
            z.string(),
            z.enum(['left', 'right']),
        ]).optional(),
        yAnchor: z.tuple([
            z.string(),
            z.enum(['top', 'bottom']),
        ]).optional(),
        width: z.number().min(1),
        height: z.union([
            z.number().min(1),
            z.literal('auto'),
        ]),
        rotation: z.number(),
        content: z.string().optional(),
        color: z.string().optional(),
        fontSize: z.number().min(1).optional(),
        fontFamily: z.string().optional(),
        fontWeight: z.string().optional(),
        textAlign: z.enum(['left', 'center', 'right']).optional(),
        lineCount: z.number().min(1).optional(),
        backgroundColor: z.string().optional(),
        borderColor: z.string().optional(),
        borderWidth: z.number().min(0).optional(),
        borderRadius: z.number().min(0).optional(),
        padding: z.tuple([
            z.number().min(0),
            z.number().min(0),
            z.number().min(0),
            z.number().min(0),
        ]).optional(),
        hasOutline: z.boolean().optional(),
        outlineColor: z.string().optional(),
        outlineWidth: z.number().min(0).optional(),
        opacity: z.number().min(0).max(1).optional(),
    }).array(),
});
